import { CHROME_Z_INDEX } from "@/contexts/ModalContext";
import { useBottomChromeSlot } from "@/hooks/use-bottom-chrome-slot";
import { cn } from "@/lib/cn";
import { useId, type ReactNode } from "react";
import { SurfaceProvider } from "./Surface";

/**
 * Width every fixed app chrome shares. Nothing in the storefront stretches to
 * 100% of the viewport: past this width the page is centered and the page
 * background shows on both sides, so a footer must not paint edge to edge
 * either.
 */
export const APP_SHELL_WIDTH = "w-full max-w-shell";

/**
 * Height of the fixed top bar. Pages that sit under it reserve exactly this
 * much (`pt-16` in HeaderMenu): deriving the bar's height from its padding plus
 * whatever the tallest icon happened to be left a 3px strip of the banner
 * showing through when the icon buttons were resized.
 */
export const APP_HEADER_HEIGHT = "h-header";

type FixedFooterProps = {
  /** Defaults to the shared chrome bar layer. */
  zIndex?: number;
  /** Applied to the white bar, not to the positioning wrapper. */
  className?: string;
  children: ReactNode;
};

/**
 * The storefront's action footer: the white bar pinned to the bottom holding a
 * screen's primary buttons (checkout steps, item modal, address form, ...).
 *
 * Two elements on purpose. The outer one only positions and spans its
 * containing block; the inner one carries the width cap and the white surface,
 * centered with a plain `mx-auto`. Centering the fixed element itself
 * (`left-1/2` + `-translate-x-1/2`) breaks inside the item modal, whose
 * animated ancestor has a `transform` and therefore becomes the containing
 * block for `position: fixed` — the bar then centers on that box instead of
 * the viewport and slides off screen.
 *
 * Each footer owns its slot in the bottom-chrome registry (`useId`) instead of
 * a shared key: the checkout step transition keeps the outgoing and the
 * incoming footer mounted at once, and a shared key let the outgoing unmount
 * delete the height the incoming had just published.
 */
export function FixedFooter({
  zIndex = CHROME_Z_INDEX.bar,
  className,
  children,
}: FixedFooterProps) {
  const measureRef = useBottomChromeSlot<HTMLDivElement>(useId());

  return (
    <footer style={{ zIndex }} className="fixed bottom-0 left-0 right-0">
      <div
        ref={measureRef}
        className={cn(
          "mx-auto",
          APP_SHELL_WIDTH,
          "bg-card shadow-bar-up",
          "px-4 pb-safe-sm pt-3",
          className,
        )}
      >
        <SurfaceProvider surface="card">{children}</SurfaceProvider>
      </div>
    </footer>
  );
}
